/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { Tier, FeatureFlag, TIER_CONFIGS } from "../types/tiers";
import { useAuth } from "../hooks/useAuth";

interface FeatureContextProps {
  currentTier: Tier;
  hasFeature: (feature: FeatureFlag) => boolean;
  upgradeTier: (tier: Tier) => void;
  isLoading: boolean;
}

const FeatureContext = createContext<FeatureContextProps>({} as FeatureContextProps);

export const FeatureProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentTier, setCurrentTier] = useState<Tier>('Merchant');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // In a real app, we would fetch the user's subscription status from the API here
    if (user) {
        // Map user role or subscription data to Tier
        // For demo purposes, we default to Merchant unless specified
        // Logic could be expanded based on user.subscription_id etc.
        setIsLoading(false);
    } else {
        setIsLoading(false);
    }
  }, [user]);

  const hasFeature = (feature: FeatureFlag): boolean => {
    const plan = TIER_CONFIGS[currentTier];
    return plan.features.includes(feature);
  };

  const upgradeTier = (tier: Tier) => {
      setCurrentTier(tier);
      // In real app, trigger backend update
      console.log(`Upgraded to ${tier}`);
  };

  return (
    <FeatureContext.Provider value={{ currentTier, hasFeature, upgradeTier, isLoading }}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatures = () => useContext(FeatureContext);